import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import apiInstance from '../config/axios';
import { getCookies } from '../utility/cookies';

interface APIConfig<TData = any, TResponse = any>
  extends Omit<AxiosRequestConfig, 'url'> {
  url: string;
  baseURL?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  dependencies?: any[];
  autoFetch?: boolean;
  onSuccess?: (data: TResponse) => void;
  onError?: (error: AxiosError<Error>) => void;
  data?: TData;
  isFormData?: boolean;
  enabled?: boolean;
}

interface APIResponse<TData, TResponse> {
  data: TResponse | null;
  error: AxiosError | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  refetch: (payload?: TData) => Promise<void>;
}

function useAPI<TData = any, TResponse = any>(
  config: APIConfig<TData, TResponse>,
): APIResponse<TData, TResponse> {
  const [data, setData] = useState<TResponse | null>(null);
  const [error, setError] = useState<AxiosError | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [currentPayload, setCurrentPayload] = useState<TData | undefined>(
    config.data,
  );

  const {
    url,
    baseURL,
    method = 'GET',
    headers = {},
    dependencies = [],
    autoFetch = method === 'GET' ? true : false,
    onSuccess,
    onError,
    isFormData = false,
    enabled = true,
    ...restConfig
  } = config;

  const resetStates = useCallback(() => {
    setIsSuccess(false);
    setIsError(false);
    setError(null);
  }, []);

  const extractData = (response: AxiosResponse): TResponse => {
    if (
      response.data &&
      typeof response.data === 'object' &&
      'data' in response.data
    ) {
      return response.data.data;
    }
    return response.data;
  };

  const fetchData = useCallback(
    async (payload?: TData): Promise<void> => {
      try {
        if (!enabled) return;
        setIsLoading(true);
        resetStates();
        const requestPayload = payload !== undefined ? payload : currentPayload;

        const accessToken = getCookies('accessToken');

        let requestHeaders = {
          Authorization: `Bearer ${accessToken}`,
          ...headers,
        };

        if (!isFormData) {
          requestHeaders = {
            ...requestHeaders,
            'Content-Type': 'application/json',
          };
        }

        const response: AxiosResponse = await apiInstance({
          url,
          method,
          headers: requestHeaders,
          ...(method !== 'GET' && requestPayload
            ? { data: requestPayload }
            : { params: requestPayload }),
          ...restConfig,
        });

        const responseData = extractData(response);

        setData(responseData);
        setIsSuccess(true);

        if (onSuccess) {
          onSuccess(responseData);
        }
      } catch (err) {
        const axiosError = err as AxiosError<Error>;
        setError(axiosError);
        setIsError(true);
        if (onError) {
          onError(axiosError);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [url, method, JSON.stringify(headers), currentPayload, enabled, baseURL],
  );

  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, [url, method, JSON.stringify(headers), baseURL, ...dependencies]);

  return {
    data,
    error,
    isLoading,
    isSuccess,
    isError,
    refetch: fetchData,
  };
}

export default useAPI;
